import { useState, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { useDealerLog, LOG_ACTIONS } from "@/hooks/useDealerLog";

const BATCH_SIZE = 25;

interface ImportRow {
  name: string;
  category: string;
  brand: string;
  code: string;
  vat: number;
  purchasePrice: number;
  salePrice: number;
  shelf: string;
  stock: number;
  unit: string;
}

function parsePrice(val: unknown): number {
  if (typeof val === "number") return val;
  if (typeof val === "string") {
    const cleaned = val.replace(/[^\d.]/g, "");
    return parseFloat(cleaned) || 0;
  }
  return 0;
}

function parseExcelRows(raw: unknown[][], XLSX: any): ImportRow[] {
  let headerIdx = -1;
  for (let i = 0; i < Math.min(raw.length, 15); i++) {
    const row = raw[i];
    if (row && String(row[0] || "").trim() === "Ürün Adı") {
      headerIdx = i;
      break;
    }
  }
  if (headerIdx === -1) {
    throw new Error("Excel dosyasında 'Ürün Adı' başlığı bulunamadı. Lütfen doğru formatta bir ürün listesi yükleyin.");
  }

  const result: ImportRow[] = [];
  for (let i = headerIdx + 1; i < raw.length; i++) {
    const row = raw[i];
    if (!row || !row[0]) continue;

    const name = String(row[0] || "").trim();
    if (!name) continue;

    const category = String(row[1] || "").trim();
    const brand = String(row[2] || "").trim();
    const code = String(row[3] || "").trim();
    const vat = Number(row[4] || 0);
    const purchasePrice = parsePrice(row[5]);
    const salePrice = parsePrice(row[6]);
    const shelf = String(row[7] || "").trim();
    const stock = Number(row[8] || 0);
    const unit = String(row[9] || "").trim();

    result.push({ name, category, brand, code, vat, purchasePrice, salePrice, shelf, stock, unit });
  }
  return result;
}

export default function DealerAdminImportPage() {
  const { user } = useAuth();
  const { logAction } = useDealerLog();
  const dealerId = user?.dealerId || user?.id || "";
  const dealerName = user?.dealerName || "";

  const [step, setStep] = useState<"upload" | "preview" | "progress" | "done" | "error">("upload");
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [progress, setProgress] = useState(0);
  const [imported, setImported] = useState(0);
  const [skipped, setSkipped] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFile = async (f: File) => {
    setErrorMsg("");
    try {
      const XLSX = await import("xlsx");
      const arrayBuffer = await f.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const rawRows = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as unknown[][];
      const parsed = parseExcelRows(rawRows, XLSX);
      if (parsed.length === 0) {
        throw new Error("Excel dosyasında işlenebilir ürün bulunamadı.");
      }
      setRows(parsed);
      setStep("preview");
    } catch (err: any) {
      setErrorMsg(err.message || "Excel dosyası okunurken hata oluştu.");
      setStep("error");
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    await processFile(f);
  };

  const startImport = async () => {
    if (!rows.length || !dealerId) return;
    setStep("progress");
    setProgress(0);
    setImported(0);
    setSkipped(0);
    setErrorMsg("");

    try {
      // Step 1: Ensure categories exist
      const categories = [...new Set(rows.map((r) => r.category).filter(Boolean))];
      const catMap: Record<string, string> = {};

      const { data: existingCats, error: catErr } = await supabase
        .from("categories")
        .select("id, name")
        .eq("dealer_id", dealerId);
      if (catErr) throw catErr;

      for (const c of existingCats || []) {
        catMap[c.name] = c.id;
      }

      for (const catName of categories) {
        if (!catMap[catName]) {
          const { data: newCat, error: insertCatErr } = await supabase
            .from("categories")
            .insert({ dealer_id: dealerId, name: catName })
            .select("id, name")
            .single();
          if (insertCatErr) throw insertCatErr;
          if (newCat) catMap[newCat.name] = newCat.id;
        }
      }

      // Step 2: Batch insert products
      const total = rows.length;
      let done = 0;
      let skipCount = 0;

      for (let i = 0; i < total; i += BATCH_SIZE) {
        const batch = rows.slice(i, i + BATCH_SIZE).map((r) => ({
          dealer_id: dealerId,
          name: r.name,
          product_code: r.code || null,
          brand: r.brand || null,
          category_id: catMap[r.category] || null,
          purchase_price: r.purchasePrice,
          sale_price: r.salePrice,
          stock: r.stock,
          shelf_location: r.shelf || null,
        }));

        const { error: insertErr } = await supabase.from("products").insert(batch);
        if (insertErr) {
          skipCount += batch.length;
        } else {
          done += batch.length;
        }

        const processed = done + skipCount;
        setImported(done);
        setSkipped(skipCount);
        setProgress(Math.round((processed / total) * 100));

        if (i + BATCH_SIZE < total) {
          await new Promise((res) => setTimeout(res, 50));
        }
      }

      setStep("done");
      logAction(dealerId, user?.id || null, LOG_ACTIONS.IMPORT, `Excel'den ${done} ürün içe aktarıldı${skipCount > 0 ? `, ${skipCount} atlandı` : ""} · Dosya: ${file?.name || "bilinmiyor"}`);
    } catch (err: any) {
      setErrorMsg(err.message || "Aktarım sırasında hata oluştu.");
      setStep("error");
    }
  };

  const reset = () => {
    setStep("upload");
    setFile(null);
    setRows([]);
    setProgress(0);
    setImported(0);
    setSkipped(0);
    setErrorMsg("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h4 className="text-lg font-semibold text-foreground-950"><a id="dealer-admin-import">Veri Aktarımı</a></h4>
        <p className="text-sm text-foreground-500 mt-0.5">Excel ürün listesini kendi bayi hesabınıza aktarın</p>
      </div>

      {errorMsg && step !== "error" && (
        <div className="bg-accent-100 border border-accent-300 text-accent-900 rounded-xl px-4 py-3 mb-4 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {errorMsg}
          <button onClick={() => setErrorMsg("")} className="ml-auto text-accent-700 font-medium cursor-pointer">Kapat</button>
        </div>
      )}

      <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
        {/* Step: Upload */}
        {step === "upload" && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center"><i className="ri-store-2-line text-accent-600"></i></div>
              <div>
                <p className="font-medium text-foreground-900 text-sm">{dealerName}</p>
                <p className="text-xs text-foreground-400">Excel ürün aktarımı · {dealerId ? "Bayi ID: " + dealerId.slice(0, 8) + "..." : "Bayi bilgisi yok"}</p>
              </div>
            </div>

            <label className="flex flex-col items-center justify-center border-2 border-dashed border-background-200/70 rounded-xl p-8 hover:border-accent-300 transition-colors cursor-pointer">
              <i className="ri-file-excel-2-line text-4xl text-foreground-300 mb-3"></i>
              <p className="text-sm font-medium text-foreground-700">Excel dosyası yükleyin</p>
              <p className="text-xs text-foreground-400 mt-1">.xlsx formatında ürün listesi</p>
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFileSelect} className="hidden" />
            </label>
          </div>
        )}

        {/* Step: Preview */}
        {step === "preview" && file && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                <i className="ri-file-excel-2-line text-primary-600"></i>
              </div>
              <div>
                <p className="font-medium text-foreground-900 text-sm">{file.name}</p>
                <p className="text-xs text-foreground-400">{(file.size / 1024).toFixed(1)} KB · {rows.length} ürün bulundu</p>
              </div>
            </div>

            <div className="bg-background-100 rounded-lg p-4 overflow-hidden">
              <p className="text-sm font-medium text-foreground-900 mb-3">Önizleme (İlk 5 ürün)</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-background-200/70 text-left">
                      <th className="px-2 py-2 font-medium text-foreground-500">Ürün Adı</th>
                      <th className="px-2 py-2 font-medium text-foreground-500">Kategori</th>
                      <th className="px-2 py-2 font-medium text-foreground-500">Marka</th>
                      <th className="px-2 py-2 font-medium text-foreground-500 text-right">Alış</th>
                      <th className="px-2 py-2 font-medium text-foreground-500 text-right">Satış</th>
                      <th className="px-2 py-2 font-medium text-foreground-500 text-center">Stok</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 5).map((r, idx) => (
                      <tr key={idx} className="border-b border-background-200/50 last:border-0">
                        <td className="px-2 py-2 text-foreground-900 font-medium truncate max-w-[200px]">{r.name}</td>
                        <td className="px-2 py-2 text-foreground-500">{r.category}</td>
                        <td className="px-2 py-2 text-foreground-500">{r.brand || "-"}</td>
                        <td className="px-2 py-2 text-foreground-500 text-right">{r.purchasePrice.toFixed(2)}</td>
                        <td className="px-2 py-2 text-primary-600 text-right font-medium">{r.salePrice.toFixed(2)}</td>
                        <td className="px-2 py-2 text-foreground-500 text-center">{r.stock}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rows.length > 5 && (
                <p className="text-xs text-foreground-400 mt-2">...ve {rows.length - 5} ürün daha</p>
              )}
            </div>

            <div className="bg-secondary-100 rounded-lg p-3 flex items-start gap-2">
              <i className="ri-information-line text-secondary-600 mt-0.5"></i>
              <div className="text-xs text-secondary-700">
                <p className="font-medium">Aktarım Bilgisi</p>
                <p>Yeni kategoriler otomatik oluşturulacak. Mevcut ürünler değişmeyecek.</p>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => { setStep("upload"); setFile(null); setRows([]); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
              >
                Geri
              </button>
              <button
                onClick={startImport}
                className="flex-1 py-2.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                Aktarımı Başlat
              </button>
            </div>
          </div>
        )}

        {/* Step: Progress */}
        {step === "progress" && (
          <div className="space-y-4 text-center py-8">
            <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-loader-4-line animate-spin text-accent-600 text-2xl"></i>
            </div>
            <h4 className="font-semibold text-foreground-950">Ürünler aktarılıyor...</h4>
            <p className="text-sm text-foreground-400">Lütfen sayfayı kapatmayın</p>
            <div className="w-full bg-background-200/70 rounded-full h-2 mt-4">
              <div className="bg-accent-500 h-2 rounded-full transition-all duration-300" style={{ width: `${progress}%` }}></div>
            </div>
            <div className="flex items-center justify-center gap-4 text-sm">
              <span className="text-foreground-500">%{progress}</span>
              <span className="text-foreground-400">|</span>
              <span className="text-primary-600 font-medium">{imported} eklendi</span>
              {skipped > 0 && (
                <>
                  <span className="text-foreground-400">|</span>
                  <span className="text-accent-600">{skipped} atlandı</span>
                </>
              )}
            </div>
          </div>
        )}

        {/* Step: Done */}
        {step === "done" && (
          <div className="text-center py-8 space-y-4">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-line text-primary-600 text-3xl"></i>
            </div>
            <h4 className="font-semibold text-foreground-950">Aktarım Başarıyla Tamamlandı!</h4>
            <div className="bg-background-100 rounded-lg p-4 inline-block text-left">
              <p className="text-sm text-foreground-600">Aktarılan kayıtlar: <strong className="text-foreground-950">{imported} ürün</strong></p>
              {skipped > 0 && (
                <p className="text-sm text-foreground-600">Atlananlar: <strong className="text-foreground-400">{skipped} ürün</strong></p>
              )}
              <p className="text-sm text-foreground-600">Bayi: <strong className="text-foreground-950">{dealerName}</strong></p>
            </div>
            <div className="flex gap-2 justify-center">
              <button
                onClick={reset}
                className="px-6 py-2.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                Yeni Aktarım
              </button>
            </div>
          </div>
        )}

        {/* Step: Error */}
        {step === "error" && (
          <div className="text-center py-8 space-y-4">
            <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-accent-600 text-3xl"></i>
            </div>
            <h4 className="font-semibold text-foreground-950">Aktarım Başarısız</h4>
            <p className="text-sm text-accent-600 bg-accent-100 rounded-lg p-3 max-w-md mx-auto">{errorMsg}</p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => { setStep("upload"); setFile(null); setRows([]); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                className="px-6 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
              >
                Tekrar Dene
              </button>
              <button
                onClick={reset}
                className="px-6 py-2.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                Baştan Başla
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}